import { useState } from 'react';
import { Button, Input, Label } from './ui';

const FRONT_URL = process.env.FRONTEND_URL || 'http://localhost:3080';

export const EmailVerification = ({ onVerified }) => {
    const [step, setStep] = useState('email'); // 'email', 'code', 'verified'
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const validateEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const handleSendCode = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (!email.trim()) {
            setError('Ingresa tu email');
            return;
        }

        if (!validateEmail(email)) {
            setError('Email inválido');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`/api/verification/send`, { //es ${FRONT_URL} ?
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: email.trim() }),
            });

            const data = await response.json();

            if (data.success) {
                setMessage('Código enviado a tu email');
                setStep('code');
            } else {
                setError(data.message || 'Error al enviar código');
            }
        } catch (err) {
            console.error('Error:', err);
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (!code.trim() || code.length !== 6) {
            setError('Ingresa el código de 6 dígitos');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`/api/verification/verify`, { //${FRONT_URL}
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    email: email.trim(), 
                    code: code.trim() 
                }),
            });

            const data = await response.json();

            if (data.success && data.verified) {
                setMessage('Email verificado correctamente');
                setStep('verified');
                onVerified(email);
            } else {
                setError(data.message || 'Código incorrecto');
            }
        } catch (err) {
            console.error('Error:', err);
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        setError('');
        setMessage('');
        setLoading(true);

        try {
            const response = await fetch(`/api/verification/send`, { //${FRONT_URL}
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: email.trim() }),
            });

            const data = await response.json();

            if (data.success) {
                setMessage('Nuevo código enviado');
            } else {
                setError(data.message || 'Error al reenviar código');
            }
        } catch (err) {
            console.error('Error:', err);
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    if (step === 'verified') {
        return (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <div className="text-green-600 text-5xl mb-4">✓</div>
                <h3 className="text-lg font-semibold text-green-800 mb-2">
                    Email verificado
                </h3>
                <p className="text-green-700">{email}</p>
                <p className="text-sm text-green-600 mt-2">
                    Ahora podés completar el formulario de solicitud
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-md p-6 max-w-md mx-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">
                Verificación de identidad
            </h2>
            <p className="text-sm text-gray-600 mb-6 text-center">
                Para evitar registros automáticos, necesitamos verificar que sos una persona real.
            </p>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            {message && (
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-4">
                    {message}
                </div>
            )}

            {step === 'email' && (
                <form onSubmit={handleSendCode} className="space-y-4">
                    <div>
                        <Label htmlFor="verify-email">Ingresa tu email</Label>
                        <Input
                            id="verify-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="tu@email.com"
                            required
                        />
                    </div>
                    <Button
                        variant="subse"
                        type="submit"
                        disabled={loading}
                        className="w-full"
                    >
                        {loading ? 'Enviando...' : 'Enviar código'}
                    </Button>
                </form>
            )}

            {step === 'code' && (
                <form onSubmit={handleVerifyCode} className="space-y-4">
                    <div>
                        <Label htmlFor="verify-code">Código de 6 dígitos</Label>
                        <Input
                            id="verify-code"
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            maxLength={6}
                            required
                            className="text-center text-2xl tracking-widest"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Te enviamos un código a {email}
                        </p>
                    </div>
                    <Button
                        type="submit"
                        disabled={loading || code.length !== 6}
                        className="w-full"
                    >
                        {loading ? 'Verificando...' : 'Verificar código'}
                    </Button>
                    <div className="text-center">
                        <button
                            type="button"
                            onClick={handleResendCode}
                            disabled={loading}
                            className="text-sm text-blue-600 hover:text-blue-800 underline"
                        >
                            Reenviar código
                        </button>
                        <span className="text-gray-400 mx-2">|</span>
                        <button
                            type="button"
                            onClick={() => setStep('email')}
                            className="text-sm text-gray-600 hover:text-gray-800"
                        >
                            Cambiar email
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};
